/**
 * Identity helpers: deterministic display colors and URL sanitization.
 */

const PEER_COLORS = [
  '#7c3aed', // violet
  '#db2777', // pink
  '#ea580c', // orange
  '#16a34a', // green
  '#0891b2', // cyan
  '#2563eb', // blue
  '#ca8a04', // yellow
  '#dc2626', // red
] as const;

/** Deterministic, pleasant display color for a pubkey. */
export function colorForPubkey(pubkey: string): string {
  let hash = 0;
  for (let i = 0; i < pubkey.length; i++) {
    hash = (hash * 31 + pubkey.charCodeAt(i)) >>> 0;
  }
  return PEER_COLORS[hash % PEER_COLORS.length];
}

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

/**
 * Sanitize an untrusted URL (e.g. from Nostr event data) to an http(s)
 * or mailto URL. Returns undefined for anything else, including
 * javascript:, data: and other dangerous schemes.
 */
export function sanitizeUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  try {
    const parsed = new URL(trimmed);
    if (ALLOWED_PROTOCOLS.has(parsed.protocol)) {
      return parsed.toString();
    }
    return undefined;
  } catch {
    // Not an absolute URL — only allow explicit relative paths.
    if (/^\/(?!\/)/.test(trimmed)) return trimmed;
    return undefined;
  }
}

/** Shorten a hex pubkey for display: bame5t…k8qx */
export function shortPubkey(pubkey: string): string {
  return `${pubkey.slice(0, 6)}…${pubkey.slice(-4)}`;
}

/** Format a unix timestamp (seconds) as a short relative time. */
export function formatRelativeTime(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (diff < 0) return date.toLocaleDateString();
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
